import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

// P012: thin wrapper around <input type="radio"> — control-first label
// (matches the existing raw report-page radio sites, e.g.
// OccupancyStatsDetailsPage's grouping filter). Group semantics (only one
// checked per `name`) are native <input> behavior — this component just
// carries `name`/`value`/`checked`/`onChange` straight through.
export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ReactNode
  variant?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error' | 'info'
  size?: 'sm' | 'md'
}

const VARIANT_CLASSES: Record<NonNullable<RadioProps['variant']>, string> = {
  primary: 'radio-primary',
  secondary: 'radio-secondary',
  accent: 'radio-accent',
  success: 'radio-success',
  warning: 'radio-warning',
  error: 'radio-error',
  info: 'radio-info',
}

const SIZE_CLASSES: Record<NonNullable<RadioProps['size']>, string> = {
  sm: 'radio-sm',
  md: '',
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ className = '', label, variant, size = 'md', disabled, ...props }, ref) => {
    const input = (
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        className={`radio ${variant ? VARIANT_CLASSES[variant] : ''} ${SIZE_CLASSES[size]} ${className}`}
        {...props}
      />
    )

    if (!label) return input

    return (
      <label className={`flex items-center gap-2 text-sm cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
        {input}
        <span>{label}</span>
      </label>
    )
  }
)

Radio.displayName = 'Radio'
